import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Clock, AlertCircle, Edit, Key, Calendar, ShieldCheck, Activity, Smartphone, FileText, CheckCircle2, XCircle, AlertTriangle, Plus, Layout, Trash2, Building2 } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useNavigate, useParams } from 'react-router-dom';
import CompanySearch from '@/components/CompanySearch';
import ProblemTypeSearch from '@/components/ProblemTypeSearch';
import RequesterSearch from '@/components/RequesterSearch';
import ClickUpTaskButton from '@/components/ClickUpTaskButton';

const BACKEND_URL = process.env.REACT_APP_API_BASE_URL;
const API = `${BACKEND_URL}/api`;

const UserDetailDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  // Create Task
  const [createTaskOpen, setCreateTaskOpen] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', description: '', problem_type: '', due_date: '', created_for_user_id: '', requested_by: '' });
  const [submittingTask, setSubmittingTask] = useState(false);
  const PROBLEM_TYPES = [
    { value: 'site_down', label: 'Site Down/Not Loading' },
    { value: 'critical_bug', label: 'Critical Bug/Error' },
    { value: 'functionality_broken', label: 'Functionality Not Working' },
    { value: 'performance_issue', label: 'Performance/Speed Issue' },
    { value: 'content_update', label: 'Content Update Request' },
    { value: 'design_change', label: 'Design/Layout Change' },
    { value: 'feature_request', label: 'New Feature Request' },
    { value: 'other', label: 'Other' },
  ];

  // Maintenance Report State
  const [maintenanceDialogOpen, setMaintenanceDialogOpen] = useState(false);
  const [newReport, setNewReport] = useState({
    month_year: new Date().toISOString().slice(0, 7),
    plugins_status: 'healthy',
    plugins_notes: '',
    security_status: 'healthy',
    security_notes: '',
    debug_log_status: 'healthy',
    debug_log_notes: '',
    layout_status: 'healthy',
    layout_notes: '',
    general_notes: '',
    time_spent: 0
  });

  const getMaintenanceBadge = (status) => {
    switch (status) {
      case 'healthy': return <Badge className="bg-green-100 text-green-700 hover:bg-green-100"><CheckCircle2 className="w-3 h-3 mr-1" /> Healthy</Badge>;
      case 'issues_found': return <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100"><AlertTriangle className="w-3 h-3 mr-1" /> Issues</Badge>;
      case 'critical': return <Badge className="bg-red-100 text-red-700 hover:bg-red-100"><XCircle className="w-3 h-3 mr-1" /> Critical</Badge>;
      default: return <Badge variant="outline">Not Checked</Badge>;
    }
  };

  const openCreateDialog = () => {
    setNewReport({
      month_year: new Date().toISOString().slice(0, 7),
      plugins_status: 'healthy',
      plugins_notes: '',
      security_status: 'healthy',
      security_notes: '',
      debug_log_status: 'healthy',
      debug_log_notes: '',
      layout_status: 'healthy',
      layout_notes: '',
      general_notes: '',
      time_spent: 0
    });
    setMaintenanceDialogOpen(true);
  };

  const handleEditReport = (report) => {
    if (report.report_source === 'company') {
      toast.info('Company maintenance reports can be edited from the company page.');
      return;
    }
    setNewReport({
      month_year: report.month_year,
      plugins_status: report.plugins_status,
      plugins_notes: report.plugins_notes || '',
      security_status: report.security_status,
      security_notes: report.security_notes || '',
      debug_log_status: report.debug_log_status,
      debug_log_notes: report.debug_log_notes || '',
      layout_status: report.layout_status,
      layout_notes: report.layout_notes || '',
      general_notes: report.general_notes || '',
      time_spent: report.time_spent || 0
    });
    setMaintenanceDialogOpen(true);
  };

  const handleCreateReport = async () => {
    try {
      await axios.post(`${API}/admin/users/${userId}/maintenance-reports`, newReport, getAuthHeaders());
      toast.success("Maintenance report saved");
      setMaintenanceDialogOpen(false);
      fetchUserDashboard();
    } catch (error) {
      toast.error("Failed to save maintenance report");
    }
  };

  const handleDeleteReport = async (reportId) => {
    const report = userData?.maintenance_reports?.find(item => item.id === reportId);
    if (report?.report_source === 'company') {
      toast.info('Company maintenance reports can be deleted from the company page.');
      return;
    }
    if (!window.confirm("Are you sure you want to delete this report? This will restore the hours used.")) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}/maintenance-reports/${reportId}`, getAuthHeaders());
      toast.success("Report deleted");
      fetchUserDashboard();
    } catch (error) {
      toast.error("Failed to delete report");
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!newTask.problem_type) {
      toast.error("Problem Type is required");
      return;
    }
    setSubmittingTask(true);
    try {
      const payload = {
        ...newTask,
        company_id: userData?.user?.company_id || null,
      };
      await axios.post(`${API}/tickets?created_for_user_id=${userId}`, payload, getAuthHeaders());
      toast.success('Task created!');
      setCreateTaskOpen(false);
      setNewTask({ title: '', description: '', problem_type: '', due_date: '', created_for_user_id: '', requested_by: '' });
      fetchUserDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create task');
    } finally {
      setSubmittingTask(false);
    }
  };

  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    company_name: '',
    website_url: '',
    company_id: null,
    is_support_client: false,
    contract_hours: 0,
    contract_amount: 0,
    role: 'user',
    clickup_list_id: null
  });
  const [companies, setCompanies] = useState([]);

  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    fetchUserDashboard();
    fetchCompanies();
  }, [userId]);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  const fetchUserDashboard = async () => {
    try {
      let url = `${API}/admin/users/${userId}/dashboard`;
      if (dateRange.start && dateRange.end) {
        url += `?start_date=${dateRange.start}T00:00:00Z&end_date=${dateRange.end}T23:59:59Z`;
      }

      const response = await axios.get(url, getAuthHeaders());
      setUserData(response.data);

      if (response.data.user) {
        setEditForm({
          name: response.data.user.name || '',
          email: response.data.user.email || '',
          company_name: response.data.user.company_name || '',
          website_url: response.data.user.website_url || '',
          company_id: response.data.user.company_id || null,
          is_support_client: response.data.user.is_support_client || false,
          contract_hours: response.data.user.contract_hours || 0,
          contract_amount: response.data.user.contract_amount || 0,
          role: response.data.user.role || 'user',
          clickup_list_id: response.data.user.clickup_list_id || null
        });
      }
    } catch (error) {
      toast.error('Failed to load user dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const res = await axios.get(`${API}/companies`, getAuthHeaders());
      setCompanies(res.data);
    } catch (e) { }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${API}/admin/users/${userId}`, editForm, getAuthHeaders());
      toast.success('User updated successfully');
      setEditDialogOpen(false);
      fetchUserDashboard();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      await axios.post(`${API}/admin/users/${userId}/change-password?new_password=${encodeURIComponent(newPassword)}`, {}, getAuthHeaders());
      toast.success('Password changed successfully');
      setPasswordDialogOpen(false);
      setNewPassword('');
    } catch (error) {
      toast.error('Failed to change password');
    }
  };

  const handleDateRangeApply = () => {
    if (dateRange.start && dateRange.end) {
      fetchUserDashboard();
    } else {
      toast.error('Please select both dates');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      open: 'bg-blue-600 text-white',
      in_progress: 'bg-yellow-500 text-white',
      resolved: 'bg-green-600 text-white',
      closed: 'bg-gray-600 text-white'
    };
    return <Badge className={`${colors[status]} font-bold uppercase`}>{status.replace('_', ' ')}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      critical: 'bg-red-600 text-white border-red-700',
      high: 'bg-orange-500 text-white border-orange-600',
      medium: 'bg-yellow-500 text-white border-yellow-600',
      low: 'bg-green-500 text-white border-green-600'
    };
    return <Badge className={`${colors[priority]} font-bold uppercase`}>{priority}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-600">User not found</p>
      </div>
    );
  }

  const hoursPercent = userData.user.contract_hours > 0
    ? (userData.hours_used_this_month / userData.user.contract_hours) * 100
    : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard')}
                className="hidden md:flex gap-2"
              >
                Dashboard
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/admin/support-clients')}
                className="hidden md:flex gap-2"
              >
                Support Clients
              </Button>
              <div className="h-6 w-px bg-gray-200 mx-2 hidden md:block"></div>
              <Button
                data-testid="back-button"
                variant="outline"
                size="sm"
                onClick={() => navigate('/admin/users')}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Users
              </Button>
            </div>

            <div className="flex items-center gap-2">
              <Button
                onClick={() => setCreateTaskOpen(true)}
                className="bg-black hover:bg-gray-800 flex items-center gap-2"
                size="sm"
              >
                <Plus className="w-4 h-4" /> Create Task
              </Button>
              <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="flex items-center gap-2">
                    <Edit className="w-4 h-4" />
                    Edit User
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Edit User Information</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleUpdateUser} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Full Name</Label>
                      <Input
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        placeholder="John Doe"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={editForm.email}
                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                        placeholder="john@example.com"
                        required
                      />
                    </div>
                    {!editForm.company_id && (
                      <div className="space-y-2">
                        <Label>Company Name</Label>
                        <Input
                          value={editForm.company_name}
                          onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })}
                          placeholder="Company Inc."
                        />
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label>Linked Company Profile</Label>
                      <CompanySearch
                        value={editForm.company_id || null}
                        onChange={(id) => setEditForm({ ...editForm, company_id: id || null })}
                        companies={companies}
                        placeholder="Search or create company..."
                      />
                      <p className="text-xs text-gray-500">Link this user to a company so their tasks are automatically associated.</p>
                    </div>
                    {!editForm.company_id && (
                      <div className="space-y-2">
                        <Label>Website URL</Label>
                        <Input
                          value={editForm.website_url}
                          onChange={(e) => setEditForm({ ...editForm, website_url: e.target.value })}
                          placeholder="https://example.com"
                        />
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label>Role</Label>
                      <select
                        value={editForm.role}
                        onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      >
                        <option value="user">User</option>
                        <option value="subadmin">Subadmin</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={editForm.is_support_client}
                        onChange={(e) => setEditForm({ ...editForm, is_support_client: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <Label>Support Client</Label>
                    </div>
                    {editForm.is_support_client && (
                      <>
                        <div className="space-y-2">
                          <Label>Contract Hours / Month</Label>
                          <Input
                            type="number"
                            step="0.5"
                            value={editForm.contract_hours}
                            onChange={(e) => setEditForm({ ...editForm, contract_hours: parseFloat(e.target.value) || 0 })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Support Contract Value ($/month)</Label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="e.g., 500"
                              className="pl-7"
                              value={editForm.contract_amount}
                              onChange={(e) => setEditForm({ ...editForm, contract_amount: parseFloat(e.target.value) || 0 })}
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>ClickUp List ID (Optional)</Label>
                          <Input
                            placeholder="Paste ClickUp List ID"
                            value={editForm.clickup_list_id || ''}
                            onChange={(e) => setEditForm({ ...editForm, clickup_list_id: e.target.value || null })}
                          />
                          <p className="text-xs text-gray-500">
                            Leave empty for default list. Find the List ID by opening the list in ClickUp and copying everything after <strong>/l/</strong> (e.g., <strong>6-901702379808-1</strong>).
                          </p>
                        </div>
                      </>
                    )}
                    <Button type="submit" className="w-full bg-black hover:bg-gray-800">
                      Save Changes
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>

              <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="flex items-center gap-2">
                    <Key className="w-4 h-4" />
                    Change Password
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Change User Password</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleChangePassword} className="space-y-4">
                    <div className="space-y-2">
                      <Label>New Password</Label>
                      <Input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Enter new password"
                        required
                      />
                    </div>
                    <Button type="submit" className="w-full bg-black hover:bg-gray-800">
                      Change Password
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="mb-8" data-testid="user-info-card">
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-2xl mb-2">
                  {userData.user.name ? `${userData.user.name} (${userData.user.email})` : userData.user.email}
                </CardTitle>
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  {userData.user.company_id ? (
                    (() => {
                      const c = companies.find(c => c.id === userData.user.company_id);
                      return c ? (
                        <div className="flex items-center gap-1.5 text-blue-700 font-medium bg-blue-50 px-2 py-0.5 rounded cursor-pointer hover:bg-blue-100 transition-colors" onClick={() => navigate(`/admin/companies/${c.id}`)}>
                          <Building2 className="w-4 h-4" /> {c.name}
                        </div>
                      ) : (
                        <span className="font-medium">{userData.user.company_name}</span>
                      );
                    })()
                  ) : userData.user.company_name && (
                    <span className="font-medium">{userData.user.company_name}</span>
                  )}
                  {userData.user.website_url && (
                    <a href={userData.user.website_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      {userData.user.website_url}
                    </a>
                  )}
                  {userData.user.is_support_client && (
                    <Badge className="bg-purple-100 text-purple-700">Support Client</Badge>
                  )}
                </div>
              </div>
              <Badge className="capitalize">{userData.user.role}</Badge>
            </div>
          </CardHeader>
        </Card>

        {/* Date Range Selector */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Custom Date Range for Hours
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                  className="mt-2"
                />
              </div>
              <div className="flex-1">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                  className="mt-2"
                />
              </div>
              <Button onClick={handleDateRangeApply} className="bg-black hover:bg-gray-800">
                Apply
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card data-testid="total-tickets-stat">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Total Tickets</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">{userData.total_tickets}</div>
            </CardContent>
          </Card>

          <Card data-testid="open-tickets-stat">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Open Tickets</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">{userData.open_tickets}</div>
            </CardContent>
          </Card>

          <Card data-testid="hours-this-month-stat">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Hours in Range</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-orange-600">{userData.hours_used_this_month.toFixed(1)}h</div>
            </CardContent>
          </Card>

          <Card data-testid="hours-remaining-stat">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Hours Remaining</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">{userData.hours_remaining.toFixed(1)}h</div>
            </CardContent>
          </Card>
        </div>

        {userData.user.is_support_client && (
          <Card className="mb-8" data-testid="hours-usage-card">
            <CardHeader>
              <CardTitle className="text-lg">Contract Hours Usage</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Used: {userData.hours_used_this_month.toFixed(1)}h</span>
                    <span className="text-gray-600">Total: {userData.user.contract_hours.toFixed(1)}h</span>
                  </div>
                  <Progress value={hoursPercent} className="h-3" />
                </div>
                {hoursPercent > 80 && (
                  <div className="flex items-center gap-2 text-sm text-orange-600">
                    <AlertCircle className="w-4 h-4" />
                    <span>Contract hours running low ({hoursPercent.toFixed(0)}% used)</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Monthly Maintenance Reports */}
        {userData.user.is_support_client && (
          <Card className="mb-8">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Activity className="w-5 h-5 text-blue-600" />
                Monthly Maintenance Reports
              </CardTitle>
              <Dialog open={maintenanceDialogOpen} onOpenChange={setMaintenanceDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-black hover:bg-gray-800" onClick={openCreateDialog}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Report
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Create or Edit Maintenance Report</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-6 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Month (YYYY-MM)</Label>
                        <Input
                          type="month"
                          value={newReport.month_year}
                          onChange={(e) => setNewReport({ ...newReport, month_year: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Time Spent (Hours)</Label>
                        <Input
                          type="number"
                          step="0.5"
                          min="0"
                          value={newReport.time_spent}
                          onChange={(e) => setNewReport({ ...newReport, time_spent: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                    </div>

                    {/* Plugins Check */}
                    <div className="space-y-3 p-4 bg-gray-50 rounded-lg border">
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-2 text-base font-semibold">
                          <FileText className="w-4 h-4" /> Plugin Updates
                        </Label>
                        <Select
                          value={newReport.plugins_status}
                          onValueChange={(val) => setNewReport({ ...newReport, plugins_status: val })}
                        >
                          <SelectTrigger className="w-[140px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="healthy">Healthy</SelectItem>
                            <SelectItem value="issues_found">Issues Found</SelectItem>
                            <SelectItem value="critical">Critical</SelectItem>
                            <SelectItem value="not_checked">Not Checked</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Textarea
                        placeholder="Notes on plugin updates..."
                        value={newReport.plugins_notes}
                        onChange={(e) => setNewReport({ ...newReport, plugins_notes: e.target.value })}
                        className="h-20 bg-white"
                      />
                    </div>

                    {/* Security Check */}
                    <div className="space-y-3 p-4 bg-gray-50 rounded-lg border">
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-2 text-base font-semibold">
                          <ShieldCheck className="w-4 h-4" /> Security Log Activity
                        </Label>
                        <Select
                          value={newReport.security_status}
                          onValueChange={(val) => setNewReport({ ...newReport, security_status: val })}
                        >
                          <SelectTrigger className="w-[140px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="healthy">Healthy</SelectItem>
                            <SelectItem value="issues_found">Issues Found</SelectItem>
                            <SelectItem value="critical">Critical</SelectItem>
                            <SelectItem value="not_checked">Not Checked</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Textarea
                        placeholder="Notes on security logs..."
                        value={newReport.security_notes}
                        onChange={(e) => setNewReport({ ...newReport, security_notes: e.target.value })}
                        className="h-20 bg-white"
                      />
                    </div>

                    {/* Debug Log Check */}
                    <div className="space-y-3 p-4 bg-gray-50 rounded-lg border">
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-2 text-base font-semibold">
                          <Activity className="w-4 h-4" /> Debug Logs
                        </Label>
                        <Select
                          value={newReport.debug_log_status}
                          onValueChange={(val) => setNewReport({ ...newReport, debug_log_status: val })}
                        >
                          <SelectTrigger className="w-[140px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="healthy">Healthy</SelectItem>
                            <SelectItem value="issues_found">Issues Found</SelectItem>
                            <SelectItem value="critical">Critical</SelectItem>
                            <SelectItem value="not_checked">Not Checked</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Textarea
                        placeholder="Notes on PHP errors/debug.log..."
                        value={newReport.debug_log_notes}
                        onChange={(e) => setNewReport({ ...newReport, debug_log_notes: e.target.value })}
                        className="h-20 bg-white"
                      />
                    </div>

                    {/* Layout Check */}
                    <div className="space-y-3 p-4 bg-gray-50 rounded-lg border">
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-2 text-base font-semibold">
                          <Layout className="w-4 h-4" /> Broken Layout (Mobile/Desktop)
                        </Label>
                        <Select
                          value={newReport.layout_status}
                          onValueChange={(val) => setNewReport({ ...newReport, layout_status: val })}
                        >
                          <SelectTrigger className="w-[140px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="healthy">Healthy</SelectItem>
                            <SelectItem value="issues_found">Issues Found</SelectItem>
                            <SelectItem value="critical">Critical</SelectItem>
                            <SelectItem value="not_checked">Not Checked</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Textarea
                        placeholder="Notes on layout check..."
                        value={newReport.layout_notes}
                        onChange={(e) => setNewReport({ ...newReport, layout_notes: e.target.value })}
                        className="h-20 bg-white"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="ghost" onClick={() => setMaintenanceDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleCreateReport} className="bg-black hover:bg-gray-800">Save Report</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {userData.maintenance_reports && userData.maintenance_reports.length > 0 ? (
                <div className="space-y-4">
                  {userData.maintenance_reports.map((report) => (
                    <div key={report.id} className="border rounded-lg p-4 bg-white shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between mb-4 pb-2 border-b">
                        <div>
                          <h4 className="font-bold text-lg flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-blue-600" />
                            {report.month_year}
                          </h4>
                          <span className="text-xs text-gray-500 block mt-1">Checked by {report.created_by_email} on {new Date(report.created_at).toLocaleDateString()}</span>
                          {report.updated_at && (
                            <span className="text-xs text-blue-600 block mt-0.5">Updated by {report.updated_by_email} on {new Date(report.updated_at).toLocaleDateString()}</span>
                          )}
                          <div className="mt-2 flex flex-wrap gap-2">
                            <Badge variant="secondary" className="text-xs font-normal">
                              <Clock className="w-3 h-3 mr-1" />
                              Time Logged: {report.time_spent}h
                            </Badge>
                            {report.report_source === 'company' && (
                              <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 text-xs font-normal">
                                Company Maintenance
                              </Badge>
                            )}
                            {report.status === 'completed' && (
                              <Badge className="bg-green-100 text-green-700 hover:bg-green-100 text-xs font-normal">
                                Completed
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <ClickUpTaskButton taskId={report.clickup_task_id} iconOnly className="h-8 w-8 p-0" />
                          <Button variant="ghost" size="sm" onClick={() => handleEditReport(report)} className="text-blue-600 hover:text-blue-800 hover:bg-blue-50">
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteReport(report.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Plugins */}
                        <div className="bg-gray-50 p-3 rounded-md">
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-medium text-sm flex items-center gap-2">
                              <FileText className="w-4 h-4 text-gray-500" /> Plugins
                            </span>
                            {getMaintenanceBadge(report.plugins_status)}
                          </div>
                          {report.plugins_notes && <p className="text-xs text-gray-600 pl-2 border-l-2 border-gray-300 italic">{report.plugins_notes}</p>}
                        </div>

                        {/* Security */}
                        <div className="bg-gray-50 p-3 rounded-md">
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-medium text-sm flex items-center gap-2">
                              <ShieldCheck className="w-4 h-4 text-gray-500" /> Security
                            </span>
                            {getMaintenanceBadge(report.security_status)}
                          </div>
                          {report.security_notes && <p className="text-xs text-gray-600 pl-2 border-l-2 border-gray-300 italic">{report.security_notes}</p>}
                        </div>

                        {/* Debug Log */}
                        <div className="bg-gray-50 p-3 rounded-md">
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-medium text-sm flex items-center gap-2">
                              <Activity className="w-4 h-4 text-gray-500" /> Debug Log
                            </span>
                            {getMaintenanceBadge(report.debug_log_status)}
                          </div>
                          {report.debug_log_notes && <p className="text-xs text-gray-600 pl-2 border-l-2 border-gray-300 italic">{report.debug_log_notes}</p>}
                        </div>

                        {/* Layout */}
                        <div className="bg-gray-50 p-3 rounded-md">
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-medium text-sm flex items-center gap-2">
                              <Layout className="w-4 h-4 text-gray-500" /> Layout
                            </span>
                            {getMaintenanceBadge(report.layout_status)}
                          </div>
                          {report.layout_notes && <p className="text-xs text-gray-600 pl-2 border-l-2 border-gray-300 italic">{report.layout_notes}</p>}
                        </div>
                      </div>

                      {report.general_notes && (
                        <div className="mt-4 pt-3 border-t">
                          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-2">General Notes</span>
                          <div className="bg-blue-50 p-3 rounded border border-blue-100 text-sm text-gray-700 whitespace-pre-wrap">
                            {report.general_notes}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg dashed border border-gray-200">
                  <Activity className="w-10 h-10 mx-auto mb-2 opacity-20" />
                  <p>No maintenance reports found.</p>
                  <Button variant="link" onClick={() => setMaintenanceDialogOpen(true)}>Start first check</Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <Card data-testid="recent-tickets-card">
          <CardHeader>
            <CardTitle className="text-lg">Recent Tickets</CardTitle>
          </CardHeader>
          <CardContent>
            {userData.recent_tickets.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No tickets yet</p>
            ) : (
              <div className="space-y-3">
                {userData.recent_tickets.map((ticket) => (
                  <div
                    key={ticket.id}
                    data-testid={`recent-ticket-${ticket.id}`}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-blue-50 hover:border hover:border-blue-200 border border-transparent transition-colors cursor-pointer"
                    title="Click to open ticket"
                    onClick={() => navigate(`/dashboard?ticketId=${ticket.id}`)}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-medium text-gray-900">{ticket.title}</h3>
                        {getPriorityBadge(ticket.priority)}
                        <Badge variant="outline" className="text-xs bg-gray-50 text-gray-600 truncate max-w-[150px]">
                          {ticket.problem_type}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600 line-clamp-1">{ticket.description}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <span>{new Date(ticket.created_at).toLocaleDateString()}</span>
                        {ticket.total_hours_spent > 0 && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {ticket.total_hours_spent}h spent
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="ml-4 flex items-center gap-2">
                      <ClickUpTaskButton taskId={ticket.clickup_task_id} iconOnly className="h-8 w-8 p-0" />
                      {getStatusBadge(ticket.status)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Create Task Dialog */}
      <Dialog open={createTaskOpen} onOpenChange={setCreateTaskOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-4 h-4" /> New Task for {userData?.user?.company_name || userData?.user?.email}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateTask} className="space-y-4">
            <div className="space-y-2">
              <Label>Problem Type</Label>
              <ProblemTypeSearch
                value={newTask.problem_type}
                onChange={(ptValue) => setNewTask({ ...newTask, problem_type: ptValue })}
                placeholder="Select problem type..."
                allowCreate={true}
              />
            </div>
            <div className="space-y-2">
              <Label>Title <span className="text-red-500">*</span></Label>
              <Input
                placeholder="Brief description of the task"
                value={newTask.title}
                onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Detailed information..."
                value={newTask.description}
                onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                rows={3}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Requested By <span className="text-gray-400 font-normal">(optional)</span></Label>
              <RequesterSearch
                value={newTask.requested_by || null}
                onChange={id => setNewTask({ ...newTask, requested_by: id || '' })}
                placeholder="Select requester..."
              />
              <p className="text-xs text-gray-500">Email notifications use the selected requester’s saved email address.</p>
            </div>
            <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-800">
              This task will be created on behalf of {userData?.user?.name || userData?.user?.email || 'this user'} and assigned to them in ClickUp.
            </div>
            <div className="space-y-2">
              <Label>Due Date <span className="text-red-500">*</span></Label>
              <Input
                type="date"
                value={newTask.due_date}
                onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                required
              />
            </div>
            <Button type="submit" className="w-full bg-black hover:bg-gray-800" disabled={submittingTask}>
              {submittingTask ? 'Creating...' : 'Create Task'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserDetailDashboard;
